import React, { useState, useRef, useEffect, forwardRef } from 'react';

import styled from 'styled-components';
import moment from 'moment';

import { useLanguage } from '../../Context/LanguageContext';
import CalcDate from '../../Model/Share/Normal/CalcDate';

import { ReactComponent as LockIconSvg } from '../../image/Lock.svg';
import { ReactComponent as UnlockIconSvg } from '../../image/Unlock.svg';

// 락커 상태 → 표시 메타데이터 //
function getLockerMeta(data, t) {
  if (data.state === 'Empty') {
    return {
      isExpired: false,
      days: '',
      displayState: t('main.locker.empty'),
      btnText: t('main.locker.startUsage')
    };
  }
  if (data.state === 'Using') {
    const isExpired = !moment(data.endDate).isAfter(moment())
      && moment(data.endDate).format('YYYY.MM.DD') !== moment().format('YYYY.MM.DD');

    return {
      isExpired,
      days: CalcDate.getDday(moment(data.endDate).format('YYYY-MM-DDT00:00:00')),
      displayState: isExpired ? t('main.locker.overused') : t('main.locker.inUse'),
      btnText: t('main.locker.endUsage')
    };
  }
  if (data.state === 'Invalid') {
    return {
      isExpired: false,
      days: '',
      displayState: t('main.locker.unavailable'),
      btnText: t('main.locker.unlock')
    };
  }
  if (data.state === 'Expected') {
    return {
      isExpired: false,
      days: '-',
      displayState: t('main.locker.expected'),
      btnText: t('main.locker.unlock')
    };
  }

  return { isExpired: false, days: '', displayState: '', btnText: '' };
}

const LockerInfoEdit = forwardRef((props, ref) => {
  const {
    index,
    lockerIndex,
    data,
    onLockChange,
    onNameChange,
    onClickLockerActionFn,
    onClickUsageHistoryFn,
    onClickChangeStateFn,
    onClickDeleteLockerFn,
    onClickChangeNameFn,
    onClickSendMessageFn,
    onClickExtendFn
  } = props;
  const { t } = useLanguage();

  const [isLocked, setIsLocked] = useState(data.state === 'Invalid');
  const [lockerName, setLockerName] = useState(data.lockerName || '');
  const [hasUserEditedName, setHasUserEditedName] = useState(false);

  const originalIsLocked = useRef(data.state === 'Invalid');
  const originalLockerName = useRef(data.lockerName || '');

  // 사용자가 직접 수정 안 한 경우, division 이름 변경에 따른 자동 이름 갱신 반영 //
  useEffect(() => {
    if (hasUserEditedName) return;

    setLockerName(data.lockerName || '');
    originalLockerName.current = data.lockerName || '';
  }, [data.lockerName, hasUserEditedName])

  // 락커 상태 기반 표시 메타데이터 //
  const { isExpired, days } = getLockerMeta(data, t);

  // 자동 생성 이름 분리: "이름 + 좌표 suffix" → prefix/suffix //
  function parseLockerName(name) {
    const match = (name || '').match(/^(.*?)(\s+[A-Z]-\d+(?:\(\d+\))?)$/);
    if (match) return { prefix: match[1], suffix: match[2] };
    return { prefix: name || '', suffix: '' };
  }

  const { prefix: lockerNamePrefix, suffix: lockerNameSuffix } = parseLockerName(lockerName);
  const isAutoMode = !hasUserEditedName && lockerNameSuffix !== '';

  function onFocusNameInput() {
    if (hasUserEditedName) return;

    setHasUserEditedName(true);
  }

  function onChangeNameInput(e) {
    const newName = e.target.value;

    setHasUserEditedName(true);
    setLockerName(newName);
    onNameChange?.(lockerIndex, newName !== originalLockerName.current, newName);
  }

  function onClickLockerStateButton() {
    const newValue = !isLocked;
    setIsLocked(newValue);
    onLockChange?.(lockerIndex, newValue !== originalIsLocked.current, newValue);
  }

  // 사용자 이름 및 D-day클릭시 //
  function onClickMidLineWrapper() {
    if (data.memberId === 0 || data.memberId === undefined) return;

    window.open('/member/valid/detail/' + data.memberId + '/locker', '_blank');
  }

  // D-Day 검사 //
  function checkDday() {
    if (days === '') return '';
    else if (days === '-') return;
    else if (moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'day') > 0) return 'D-' + moment(moment(data.endDate).format('YYYY-MM-DD')).diff(moment(moment().format('YYYY-MM-DD')), 'day');
    else if (moment(data.endDate).format('YYYY.MM.DD') === moment().format('YYYY.MM.DD')) return 'D-Day';
    else return 'D+' + moment(moment().format('YYYY-MM-DD')).diff(moment(moment(data.endDate).format('YYYY-MM-DD')), 'day');
  }

  return (
    <TopLevelWrapper>
      <InfoBox $state={data.state} $isExpired={isExpired} $isLocked={isLocked}>
        {
          !isLocked &&
          <InfoBoxInnerWrapper>
            <TopWrapper>
              <NameInputWrapper>
                <NameInput
                  value={isAutoMode ? lockerNamePrefix : lockerName}
                  onFocus={onFocusNameInput}
                  onChange={onChangeNameInput}
                />
                {
                  isAutoMode &&
                  <NameSuffix>{lockerNameSuffix}</NameSuffix>
                }
              </NameInputWrapper>
              {
                data.state !== 'Empty' &&
                <MidLineWrapper onClick={onClickMidLineWrapper}>
                  <NameWrapper>
                    <Name>{data.userName}</Name>
                  </NameWrapper>
                  <Dday $isOverused={isExpired}>{checkDday()}</Dday>
                </MidLineWrapper>
              }
            </TopWrapper>
          </InfoBoxInnerWrapper>
        }
        {
          (data.state === 'Empty' || data.state === 'Invalid') &&
          <LockerButton onClick={onClickLockerStateButton} $isLocked={isLocked} >
            {isLocked ? <LockIcon /> : <UnlockIcon />}
          </LockerButton>
        }
      </InfoBox>
    </TopLevelWrapper>
  )
});

export default LockerInfoEdit;

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoBox = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: space-between;
  align-items: center;

  padding: 10px;

  box-sizing: border-box;
  width: 122px;
  height: 122px;

  border: ${props => {
    if (props.$isLocked) return 'none';
    if (props.$state === 'Using' && props.$isExpired) return `1px solid ${props.theme.pointRed}`;
    if (props.$state === 'Using') return `1px solid ${props.theme.line}`;
    return `1px solid ${props.theme.line}`;
  }};
  background-image: ${props => {
    if (!props.$isLocked) return 'none';
    const color = encodeURIComponent(props.theme.pointRed);
    return `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='${color}' stroke-width='2' stroke-dasharray='8%2c 12' stroke-linecap='square'/%3e%3c%2fsvg%3e")`;
  }};
  border-radius: 12px;
  background-color:  ${props => props.$isLocked ? 'none' : props.theme.subBackground};

  overflow: hidden;

  transition: all 0.3s ease-in-out;
`;

const InfoBoxInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`

const TopWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const NameInputWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 0px 10px;
  gap: 2px;

  width: 100%;
  height: 31px;

  box-sizing: border-box;

  background-color: ${props => props.theme.subBackground};
  background-image: ${props => {
    const color = encodeURIComponent(props.theme.line);
    return `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' viewBox='-0.5 -0.5 103 32' preserveAspectRatio='none' xmlns='http://www.w3.org/2000/svg'%3e%3crect x='0' y='0' width='102' height='31' fill='none' rx='5' ry='5' stroke='${color}' stroke-width='1' stroke-dasharray='9.7%2c 8.4' stroke-linecap='square'/%3e%3c/svg%3e")`;
  }};
  border-radius: 5px;

  flex: none;
  align-self: stretch;
  flex-grow: 1;
`;

const NameInput = styled.input`
  flex: 1;
  min-width: 0;

  font-family: NotoSansKR-Medium;
  font-size: 14px;
  color: ${props => props.theme.text};

  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;

  text-overflow: ellipsis;
`;

const NameSuffix = styled.span`
  flex-shrink: 0;

  font-family: NotoSansKR-Medium;
  font-size: 14px;
  color: ${props => props.theme.text};

  white-space: nowrap;
`;

const MidLineWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: space-between;
  align-items: flex-start;

  margin-top: 6px;

  width: 100%;
  height: 46px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const NameWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: 20px;

  overflow: hidden;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const Name = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;
`;

const Dday = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.$isOverused ? props.theme.pointRed : props.theme.text};

  margin: 0 0 0 0;
`;

const LockerButton = styled.div`
  position: absolute;
  bottom: 0px;
  right: 0px;

  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 30px;
  height: 30px;

  border-radius: 5px 0 5px 0;

  background-color: ${props => props.$isLocked ? props.theme.pointRedAlpha10 : props.theme.buttonFill3};
`;


const LockIcon = styled(LockIconSvg)`
  width: 11px;
  height: 14px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.pointRed};
  }
`;

const UnlockIcon = styled(UnlockIconSvg)`
  width: 11px;
  height: 16px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.subText};
  }
`;
